import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';

import { AgentForm } from '@components/agents';
import { queryKeys } from '@constants/queryKeys.constant';
import { defaultVoiceSettings } from '@constants/voiceSettings.constant';
import { ETonePreset } from '@enums/tonePreset.enum';
import { getErrorMessage } from '@helpers/getErrorMessage.helper';
import type { AgentConfigDto } from '@services/agents/dtos/agent.dto';
import { agentsService } from '@services/agents/service';

export const Route = createFileRoute('/_dashboard/agents/new')({
  component: NewAgentPage,
});

const defaultValues: AgentConfigDto = {
  goal: '',
  initialInstructions: '',
  language: 'en',
  name: '',
  openingMessage: '',
  toneInstructions: '',
  tonePreset: ETonePreset.Friendly,
  voiceSettings: defaultVoiceSettings,
};

function NewAgentPage() {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation({
    mutationFn: (values: AgentConfigDto) => agentsService.create(values),
    onError: error => {
      toast.error('Warning', { description: getErrorMessage(error) });
    },
    onSuccess: async agent => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      navigate({ params: { agentId: agent.id }, to: '/agents/$agentId' });
      toast.success('Agent created', {
        description:
          'Continue with workflow, knowledge and publishing settings.',
      });
    },
  });

  return (
    <section
      className="grid min-h-full content-start gap-8"
      data-slot="new-agent-page"
    >
      <header>
        <h1 className="text-primary-dark text-3xl leading-tight font-extrabold md:text-4xl">
          New agent
        </h1>
        <p className="text-muted mt-3 max-w-2xl text-sm leading-6 md:text-base">
          Configure the prompt, goal, tone of voice and call sensitivity for a
          new agent.
        </p>
      </header>

      <AgentForm
        defaultValues={defaultValues}
        isSubmitting={isPending}
        onSubmit={mutate}
        submitLabel="Create agent"
      />
    </section>
  );
}
